/* eslint-disable no-restricted-imports */
import { Suspense } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { Spinner } from '@/components/Elements';
import {
  MainLayout,
  StudentPortalLayout,
  AdminPortalLayout,
} from '@/components/Layout';
import { About } from '@/features/about';
// import { CourseDetails } from '@/features/academy';
import { Courses } from '@/features/admin/courses';
import { Transactions } from '@/features/admin/transactions';
import { Users } from '@/features/admin/users';
import { UpdatePassword } from '@/features/auth/routes/UpdatePassword';
import { BuyCrypto } from '@/features/buycrypto';
import { Mentorship } from '@/features/mentorship';
import { lazyImport } from '@/utils/lazyImport';

// const { Academy } = lazyImport(() => import('@/features/academy'), 'Academy');
const { Dashboard } = lazyImport(
  () => import('@/features/dashboard'),
  'Dashboard',
);
const { Wallet } = lazyImport(() => import('@/features/wallet'), 'Wallet');

const { RealEstate } = lazyImport(
  () => import('@/features/realEstate'),
  'RealEstate',
);

const { AdminRealEstateUserRequest } = lazyImport(
  () => import('@/features/admin/realEstateUserRequest'),
  'AdminRealEstateUserRequest',
);

const { SaleRequests } = lazyImport(
  () => import('@/features/sale-request'),
  'SaleRequests',
);

const { MyRealEstate } = lazyImport(
  () => import('@/features/myRealEstate'),
  'MyRealEstate',
);

const { MarketShare } = lazyImport(
  () => import('@/features/marketShare'),
  'MarketShare',
);

const { Portfolio } = lazyImport(
  () => import('@/features/portfolio'),
  'Portfolio',
);

const { Savings } = lazyImport(() => import('@/features/savings'), 'Savings');
const { MyInvestments } = lazyImport(
  () => import('@/features/myInvestments'),
  'MyInvestments',
);

const { Loans } = lazyImport(() => import('@/features/loans'), 'Loans');

const { MyTeam } = lazyImport(() => import('@/features/myTeam'), 'MyTeam');

const { Settings } = lazyImport(
  () => import('@/features/settings'),
  'Settings',
);

const { Dashboard: AdminDashboard } = lazyImport(
  () => import('@/features/admin/dashboard'),
  'Dashboard',
);

const { Investments: AdminInvestments } = lazyImport(
  () => import('@/features/admin/investments'),
  'Investments',
);

const { Mentorships: AdminMentorship } = lazyImport(
  () => import('@/features/admin/mentorship'),
  'Mentorships',
);

const { Loans: AdminLoans } = lazyImport(
  () => import('@/features/admin/loans'),
  'Loans',
);

const { Cashout } = lazyImport(
  () => import('@/features/admin/transactions'),
  'Cashout',
);

const { Cryptos } = lazyImport(
  () => import('@/features/admin/crypto'),
  'Cryptos',
);

const { Config } = lazyImport(
  () => import('@/features/admin/config'),
  'Config',
);
const { Share } = lazyImport(() => import('@/features/admin/share'), 'Share');
const { Activities } = lazyImport(
  () => import('@/features/admin/ActivityLogs'),
  'Activities',
);

const { AdminRealEstate } = lazyImport(
  () => import('@/features/admin/realEstate'),
  'AdminRealEstate',
);

const { SaleRequest } = lazyImport(
  () => import('@/features/admin/realEstateRequest'),
  'SaleRequest',
);

const { ProjectSlots } = lazyImport(
  () => import('@/features/projectSlots'),
  'ProjectSlots',
);

const { MyProjectSlots } = lazyImport(
  () => import('@/features/myProjectSlots'),
  'MyProjectSlots',
);

const { AdminProjectSlots } = lazyImport(
  () => import('@/features/admin/projectSlots'),
  'AdminProjectSlots',
);

const App = () => {
  const location = useLocation();
  const page: string = location.pathname.split('/app/')[1];

  return (
    <MainLayout page={page}>
      <Suspense
        fallback={
          <div className='h-full w-full flex items-center justify-center'>
            <Spinner size='md' />
          </div>
        }
      >
        <Outlet />
      </Suspense>
    </MainLayout>
  );
};

const StudentPortal = () => {
  return (
    <StudentPortalLayout>
      <Suspense
        fallback={
          <div className='h-full w-full flex items-center justify-center'>
            <Spinner size='md' />
          </div>
        }
      >
        <Outlet />
      </Suspense>
    </StudentPortalLayout>
  );
};

const AdminPortal = () => {
  return (
    <AdminPortalLayout>
      <Suspense
        fallback={
          <div className='h-full w-full flex items-center justify-center'>
            <Spinner size='md' />
          </div>
        }
      >
        <Outlet />
      </Suspense>
    </AdminPortalLayout>
  );
};

export const protectedRoutes = [
  { path: '/auth/change-password', element: <UpdatePassword /> },
  {
    path: '/app',
    element: <App />,
    children: [
      // { path: 'academy', element: <Academy /> },
      // { path: 'academy/courses', element: <CourseDetails /> },
      { path: 'about', element: <About /> },
      { path: 'mentorship', element: <Mentorship /> },
      { path: 'buycrypto', element: <BuyCrypto /> },
      { path: '*', element: <Navigate to='.' /> },
    ],
  },
  {
    path: '/portal',
    element: <StudentPortal />,
    children: [
      { path: 'dashboard', element: <Dashboard /> },
      { path: 'wallet', element: <Wallet /> },
      { path: 'my-slot', element: <MyInvestments /> },
      { path: 'real-estate', element: <RealEstate /> },
      { path: 'sale-requests', element: <SaleRequests /> },
      { path: 'my-properties', element: <MyRealEstate /> },
      { path: 'project-unit-market', element: <MarketShare /> },
      { path: 'project-slots', element: <ProjectSlots /> },
      { path: 'my-project-slots', element: <MyProjectSlots /> },
      { path: 'portfolio', element: <Portfolio /> },
      { path: 'savings', element: <Savings /> },
      { path: 'loans', element: <Loans /> },
      { path: 'my-team', element: <MyTeam /> },
      { path: 'settings', element: <Settings /> },
    ],
  },
  {
    path: '/admin',
    element: <AdminPortal />,
    children: [
      {
        path: 'dashboard',
        element: <AdminDashboard />,
      },
      {
        path: 'users',
        element: <Users />,
      },
      {
        path: 'transactions',
        element: <Transactions />,
      },

      {
        path: 'investments',
        element: <AdminInvestments />,
      },

      {
        path: 'real-estate',
        element: <AdminRealEstate />,
      },
      {
        path: 'real-estate/user-requests',
        element: <AdminRealEstateUserRequest />,
      },
      {
        path: 'sale-requests',
        element: <SaleRequest />,
      },
      {
        path: 'mentorship',
        element: <AdminMentorship />,
      },
      {
        path: 'loans',
        element: <AdminLoans />,
      },
      {
        path: 'cashout',
        element: <Cashout />,
      },
      {
        path: 'crypto',
        element: <Cryptos />,
      },
      {
        path: 'config',
        element: <Config />,
      },
      {
        path: 'project-unit',
        element: <Share />,
      },
      {
        path: 'project-slots',
        element: <AdminProjectSlots />,
      },
      {
        path: 'activities',
        element: <Activities />,
      },
      {
        path: 'kyc',
        element: <Courses />,
      },
    ],
  },
];
